import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_URL } from "./baseApi";
import {
  ResponderRequest,
  ResponderResponse,
  ConfirmResponderRequest,
  ConfirmResponderResponse,
} from "types/authTypes";
import {
  AnswerRequest,
  AssessmentResponse,
  OrganizationAnswerRequest,
  OrganizationAssessmentResponse,
  QuestionResponse,
  Responder,
  RuleCriteria,
} from "types/assessmentTypes";
import { PaginationArgs } from "types/globalTypes";
import { buildQueryParams } from "services/helper";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import i18n from "i18n";

const responderBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,

  prepareHeaders: (headers) => {
    const token = sessionStorage.getItem("responder-token");

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

const responderBaseQueryWithNotifications = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  const requestMeta =
    typeof args === "object" && "meta" in args ? args.meta : {};

  const meta = {
    showSuccess: true,
    showError: true,
    ...requestMeta,
  };

  const result = await responderBaseQuery(args, api, extraOptions);

  // error notif
  if (result.error && meta.showError) {
    const error: any = result.error;

    let message = i18n.t("api.defaultError");
    let title;

    if (Array.isArray(error?.data?.message)) {
      message = error.data.message.join("\n");

      title =
        (error.data.error || "Error") +
        (error.data.statusCode ? ` (${error.data.statusCode})` : "");
    } else if (error?.data?.message) {
      message = error.data.message;

      title =
        (error.data.error || "Error") +
        (error.data.statusCode ? ` (${error.data.statusCode})` : "");
    } else if (Array.isArray(error?.data?.errors)) {
      message = error.data.errors.join("\n");
    } else if (error?.status === "FETCH_ERROR") {
      message = i18n.t("api.networkError");
    }

    api.dispatch(
      addNotification({
        message,
        title,
        type: "error",
      })
    );
  }

  if (result.data && meta.showSuccess) {
    const data: any = result.data;

    if (data?.message) {
      api.dispatch(
        addNotification({
          type: "success",
          title: i18n.t("api.successTitle"),
          message: data.message,
        })
      );
    }
  }

  return result;
};

export const responderApi = createApi({
  reducerPath: "responderApi",

  baseQuery: responderBaseQueryWithNotifications,

  tagTypes: [
    "ResponderProfile",
    "ResponderAuth",
    "ResponderAssessment",
    "ResponderOrganizationAssessment",
    "Question",
    "RuleCriteria",
  ],

  endpoints: (builder) => ({
    // profile
    getResponderProfile: builder.query<Responder, void>({
      query: () => "/responder-profile",
      providesTags: ["ResponderProfile"],
    }),

    updateResponderProfile: builder.mutation<
      { data: Responder },
      Partial<Responder>
    >({
      query: (newData) => ({
        url: "/responder-profile",
        method: "PUT",
        body: newData,
      }),
      invalidatesTags: ["ResponderProfile"],
    }),

    // auth
    responderLogin: builder.mutation<ResponderResponse, ResponderRequest>({
      query: (body) => ({
        url: "/responder-auth/login",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ResponderAuth"],
    }),

    confirmResponderLogin: builder.mutation<
      ConfirmResponderResponse,
      ConfirmResponderRequest
    >({
      query: (body) => ({
        url: "/responder-auth/confirm",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ResponderAuth"],
    }),

    resendResponderOtp: builder.mutation<void, { mobile: string }>({
      query: (body) => ({
        url: "/responder-auth/resend-token",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ResponderAuth"],
    }),

    // assessment
    getResponderAssessments: builder.query<AssessmentResponse, PaginationArgs>({
      query: (args) => ({
        url: `/responder/assessment?${buildQueryParams(args || {})}`,
      }),
      providesTags: ["ResponderAssessment"],
    }),

    getOrganizationAssessmentsForResponder: builder.query<
      OrganizationAssessmentResponse,
      PaginationArgs
    >({
      query: (args) => ({
        url: `/responder/assessment/organization-assessments?${buildQueryParams(
          args || {}
        )}`,
      }),
      providesTags: ["ResponderOrganizationAssessment"],
    }),

    getQuestions: builder.query<QuestionResponse, PaginationArgs>({
      query: (args) => ({
        url: `/question?${buildQueryParams(args || {})}`,
      }),
      providesTags: ["Question"],
    }),

    setAnswers: builder.mutation<void, AnswerRequest>({
      query: (body) => ({
        url: "/answer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ResponderAssessment"],
    }),

    setOrganizationAnswers: builder.mutation<void, OrganizationAnswerRequest>({
      query: (body) => ({
        url: "/answer/organization-answer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ResponderOrganizationAssessment"],
    }),

    // role criteria
    getRuleCriteria: builder.query<RuleCriteria[], PaginationArgs>({
      query: (args) => {
        return {
          url: `/rule-criteria?${buildQueryParams(args || {})}`,
        };
      },
      providesTags: ["RuleCriteria"],
    }),
  }),
});

export const {
  // profile
  useGetResponderProfileQuery,
  useUpdateResponderProfileMutation,
  // auth
  useResponderLoginMutation,
  useConfirmResponderLoginMutation,
  useResendResponderOtpMutation,
  // assessment
  useGetResponderAssessmentsQuery,
  useGetOrganizationAssessmentsForResponderQuery,
  useGetQuestionsQuery,
  useSetAnswersMutation,
  useSetOrganizationAnswersMutation,
  // role criteria
  useGetRuleCriteriaQuery,
} = responderApi;
